/**
 * 스프레드시트 초기화 서비스
 *
 * Google Sheets 스프레드시트와 필요한 시트들을 자동으로 생성하고
 * 기본 데이터를 초기화하는 개발자 도구입니다.
 *
 * 주요 기능:
 * 1. 새 스프레드시트 생성 + 5개 시트 초기화
 * 2. 기존 스프레드시트에 필요한 시트만 추가 (백업 전략 포함)
 * 3. 모드별 데이터 삽입 (test/production)
 * 4. 진행 상황 추적 (7단계)
 */

import { GoogleSheetsClient } from '@/core/api/GoogleSheetsClient'
import type { AuthUser } from '@/core/auth/types'
import { SHEET_NAMES } from '@/core/config/constants'
import {
  convertCategoriesToSheetRows,
  convertPaymentMethodsToSheetRows,
  convertUsersToSheetRows,
  convertBudgetsToSheetRows,
  convertTransactionsToSheetRows,
} from '@/shared/utils/sheetDataConverter'

// Mock 데이터 import
import { expenseCategories, incomeCategories } from '@/domains/category/mocks/mockCategories'
import { mockPaymentMethods } from '@/domains/payment/mocks/mockPaymentMethods'
import { mockUsers } from '@/domains/user/mocks/mockUsers'
import { mockBudgets } from '@/domains/budget/mocks/mockBudgets'
import { mockTransactions } from '@/domains/transaction/mocks/mockTransactions'

/**
 * 초기화 진행 단계
 */
export type InitStep =
  | 'preparing' // 준비 중
  | 'creating_spreadsheet' // 스프레드시트 생성
  | 'checking_sheets' // 기존 시트 확인
  | 'backing_up_sheets' // 시트 백업
  | 'creating_sheets' // 시트 생성
  | 'deleting_default_sheet' // 기본 시트 삭제
  | 'inserting_data' // 데이터 삽입
  | 'finalizing' // 마무리
  | 'completed' // 완료

/**
 * 초기화 진행 정보
 */
export interface InitProgress {
  step: InitStep
  currentSheet?: string
  totalSheets: number
  completedSheets: number
  message: string
  percentage: number
}

/**
 * 초기화 결과
 */
export interface InitResult {
  success: boolean
  spreadsheetId?: string
  message: string
  backedUpSheets?: string[] // 백업된 시트 이름들 (하위 호환성 유지)
  existingSheets?: string[] // 기존에 있던 시트 목록
  createdSheets?: string[] // 새로 생성된 시트 목록
}

/**
 * 시트 설정 정보
 */
interface SheetConfig {
  name: string
  headers: string[]
}

/**
 * 초기화 모드
 */
export type InitMode = 'test' | 'production'

/**
 * 스프레드시트 초기화 클래스
 */
export class SpreadsheetInitializer {
  private client: GoogleSheetsClient
  private mode: InitMode
  private currentUser: AuthUser | null

  /**
   * 6개 시트 설정 정보 (README 포함)
   */
  private readonly SHEET_CONFIGS: SheetConfig[] = [
    {
      name: SHEET_NAMES.README,
      headers: ['Type', 'Value'],
    },
    {
      name: SHEET_NAMES.CATEGORIES,
      headers: [
        'id',
        'name',
        'icon',
        'type',
        'level',
        'parentId',
        'sortOrder',
        'isDefault',
        'isActive',
      ],
    },
    {
      name: SHEET_NAMES.PAYMENT_METHODS,
      headers: ['id', 'name', 'type', 'ownerId', 'memo', 'sortOrder', 'isActive'],
    },
    {
      name: SHEET_NAMES.USERS,
      headers: ['id', 'email', 'name', 'role', 'isActive', 'joinedAt'],
    },
    {
      name: SHEET_NAMES.BUDGETS,
      headers: ['id', 'categoryId', 'parentBudgetId', 'amount', 'year', 'month', 'isActive'],
    },
    {
      name: SHEET_NAMES.TRANSACTIONS,
      headers: [
        'id',
        'date',
        'type',
        'majorCategoryId',
        'minorCategoryId',
        'amount',
        'paymentMethodId',
        'spenderId',
        'writerId',
        'memo',
        'createdAt',
        'updatedAt',
      ],
    },
  ]

  constructor(
    client: GoogleSheetsClient,
    mode: InitMode,
    currentUser: AuthUser | null
  ) {
    this.client = client
    this.mode = mode
    this.currentUser = currentUser
  }

  /**
   * 새 스프레드시트 생성 및 초기화
   *
   * 완전히 새로운 Google Sheets 스프레드시트를 생성하고
   * 모든 시트 + 헤더 + 기본 데이터를 초기화합니다.
   *
   * @param title - 스프레드시트 제목 (예: "우리 지갑 - 홍길동")
   * @param onProgress - 진행 상황 콜백
   * @returns 생성된 spreadsheetId
   *
   * @example
   * ```typescript
   * const initializer = new SpreadsheetInitializer(client, 'production', currentUser)
   * const spreadsheetId = await initializer.initializeNewSpreadsheet(
   *   "우리 지갑 - 홍길동",
   *   (progress) => {
   *     console.log(`${progress.percentage}% - ${progress.message}`)
   *   }
   * )
   * console.log(`생성된 Spreadsheet ID: ${spreadsheetId}`)
   * ```
   */
  async initializeNewSpreadsheet(
    title: string,
    onProgress?: (progress: InitProgress) => void
  ): Promise<InitResult> {
    try {
      const totalSteps = 8
      let currentStep = 0

      // Step 1: 준비
      currentStep++
      onProgress?.({
        step: 'preparing',
        totalSheets: this.SHEET_CONFIGS.length,
        completedSheets: 0,
        message: '초기화 준비 중...',
        percentage: Math.round((currentStep / totalSteps) * 100),
      })

      // Step 2: 스프레드시트 생성
      currentStep++
      onProgress?.({
        step: 'creating_spreadsheet',
        totalSheets: this.SHEET_CONFIGS.length,
        completedSheets: 0,
        message: '스프레드시트 생성 중...',
        percentage: Math.round((currentStep / totalSteps) * 100),
      })

      const spreadsheetId = await GoogleSheetsClient.createSpreadsheet(title)

      // 생성된 스프레드시트를 사용하도록 클라이언트 재생성
      this.client = new GoogleSheetsClient(spreadsheetId)

      // Step 3: 기존 시트 확인 (새로 생성했으므로 "Sheet1"만 있음)
      currentStep++
      onProgress?.({
        step: 'checking_sheets',
        totalSheets: this.SHEET_CONFIGS.length,
        completedSheets: 0,
        message: '기존 시트 확인 중...',
        percentage: Math.round((currentStep / totalSteps) * 100),
      })

      // Step 4: 백업 (신규 생성이므로 스킵)
      currentStep++
      onProgress?.({
        step: 'backing_up_sheets',
        totalSheets: this.SHEET_CONFIGS.length,
        completedSheets: 0,
        message: '백업 단계 (신규 생성으로 스킵)...',
        percentage: Math.round((currentStep / totalSteps) * 100),
      })

      // Step 5: 시트 생성
      currentStep++
      for (let i = 0; i < this.SHEET_CONFIGS.length; i++) {
        const config = this.SHEET_CONFIGS[i]
        if (!config) continue // TypeScript 타입 가드

        onProgress?.({
          step: 'creating_sheets',
          currentSheet: config.name,
          totalSheets: this.SHEET_CONFIGS.length,
          completedSheets: i,
          message: `시트 생성 중: ${config.name} (${i + 1}/${this.SHEET_CONFIGS.length})`,
          percentage: Math.round((currentStep / totalSteps) * 100),
        })

        await this.client.createSheet(config.name, config.headers)
      }

      // Step 6: 기본 Sheet1/시트1 삭제
      currentStep++
      onProgress?.({
        step: 'deleting_default_sheet',
        totalSheets: this.SHEET_CONFIGS.length,
        completedSheets: this.SHEET_CONFIGS.length,
        message: '기본 시트 정리 중...',
        percentage: Math.round((currentStep / totalSteps) * 100),
      })

      try {
        // Sheet1 또는 시트1 찾아서 삭제
        const sheets = await this.client.getSpreadsheetMetadata()
        const defaultSheet = sheets.find((s) => s.title === 'Sheet1' || s.title === '시트1')

        if (defaultSheet) {
          await this.client.deleteSheet(defaultSheet.title)
          console.log(`기본 시트 삭제 완료: ${defaultSheet.title}`)
        }
      } catch (error) {
        // 삭제 실패 시 README를 첫 번째로 이동
        console.warn('기본 시트 삭제 실패, README를 첫 번째로 이동합니다.', error)
        try {
          await this.client.moveSheetToFirst('README')
        } catch (moveError) {
          console.error('README 시트 이동 실패', moveError)
          // 이동도 실패하면 무시하고 계속 진행
        }
      }

      // Step 7: 데이터 삽입
      currentStep++
      onProgress?.({
        step: 'inserting_data',
        totalSheets: this.SHEET_CONFIGS.length,
        completedSheets: this.SHEET_CONFIGS.length,
        message: '기본 데이터 삽입 중...',
        percentage: Math.round((currentStep / totalSteps) * 100),
      })

      await this.insertDefaultData()

      // Step 8: 마무리
      currentStep++
      onProgress?.({
        step: 'finalizing',
        totalSheets: this.SHEET_CONFIGS.length,
        completedSheets: this.SHEET_CONFIGS.length,
        message: '초기화 완료',
        percentage: 100,
      })

      onProgress?.({
        step: 'completed',
        totalSheets: this.SHEET_CONFIGS.length,
        completedSheets: this.SHEET_CONFIGS.length,
        message: '모든 작업이 완료되었습니다',
        percentage: 100,
      })

      return {
        success: true,
        spreadsheetId,
        message: `스프레드시트가 성공적으로 생성되었습니다. ID: ${spreadsheetId}`,
      }
    } catch (error) {
      return {
        success: false,
        message: `초기화 실패: ${error instanceof Error ? error.message : '알 수 없는 오류'}`,
      }
    }
  }

  /**
   * 기존 스프레드시트에 시트 추가 (백업 전략 포함)
   *
   * 이미 설정된 SPREADSHEET_ID에 필요한 시트들을 추가합니다.
   * 동일한 이름의 시트가 있으면 백업 후 새로 생성합니다.
   *
   * @param onProgress - 진행 상황 콜백
   *
   * @example
   * ```typescript
   * const initializer = new SpreadsheetInitializer(client, 'production', currentUser)
   * await initializer.addSheetsToExisting((progress) => {
   *   console.log(`${progress.percentage}% - ${progress.message}`)
   * })
   * ```
   */
  async addSheetsToExisting(onProgress?: (progress: InitProgress) => void): Promise<InitResult> {
    try {
      const totalSteps = 7
      let currentStep = 0

      // Step 1: 준비
      currentStep++
      onProgress?.({
        step: 'preparing',
        totalSheets: this.SHEET_CONFIGS.length,
        completedSheets: 0,
        message: '초기화 준비 중...',
        percentage: Math.round((currentStep / totalSteps) * 100),
      })

      // Step 2: 스프레드시트 생성 (기존 사용이므로 스킵)
      currentStep++
      onProgress?.({
        step: 'creating_spreadsheet',
        totalSheets: this.SHEET_CONFIGS.length,
        completedSheets: 0,
        message: '스프레드시트 확인 중 (기존 사용)...',
        percentage: Math.round((currentStep / totalSteps) * 100),
      })

      // Step 3: 기존 시트 확인
      currentStep++
      onProgress?.({
        step: 'checking_sheets',
        totalSheets: this.SHEET_CONFIGS.length,
        completedSheets: 0,
        message: '기존 시트 확인 중...',
        percentage: Math.round((currentStep / totalSteps) * 100),
      })

      const existingSheets = await this.client.getSpreadsheetMetadata()
      const existingSheetNames = new Set(existingSheets.map((s) => s.title))

      // 변경 이력: 2025-12-16
      // - 기존 시트 보존 로직 추가 (백업 기능 제거)
      // - 없는 시트만 새로 생성하도록 개선
      // - Issue: "기존 스프레드시트 연결 시 데이터 손실" 해결

      // Step 4: 기존 시트와 신규 시트 분류
      currentStep++
      const existingSheetsFound = this.SHEET_CONFIGS.filter((config) =>
        existingSheetNames.has(config.name)
      )
      const sheetsToCreate = this.SHEET_CONFIGS.filter((config) =>
        !existingSheetNames.has(config.name)
      )

      // 기존 시트 확인 메시지 (백업하지 않음)
      if (existingSheetsFound.length > 0) {
        onProgress?.({
          step: 'backing_up_sheets', // 단계 이름은 호환성을 위해 유지
          totalSheets: this.SHEET_CONFIGS.length,
          completedSheets: 0,
          message: `${existingSheetsFound.length}개 기존 시트 확인됨 (유지)`,
          percentage: Math.round((currentStep / totalSteps) * 100),
        })
      } else {
        onProgress?.({
          step: 'backing_up_sheets',
          totalSheets: this.SHEET_CONFIGS.length,
          completedSheets: 0,
          message: '모든 시트를 새로 생성합니다',
          percentage: Math.round((currentStep / totalSteps) * 100),
        })
      }

      // Step 5: 없는 시트만 생성
      currentStep++
      for (let i = 0; i < sheetsToCreate.length; i++) {
        const config = sheetsToCreate[i]
        if (!config) continue // TypeScript 타입 가드

        onProgress?.({
          step: 'creating_sheets',
          currentSheet: config.name,
          totalSheets: this.SHEET_CONFIGS.length,
          completedSheets: existingSheetsFound.length + i,
          message: `시트 생성 중: ${config.name} (${i + 1}/${sheetsToCreate.length})`,
          percentage: Math.round((currentStep / totalSteps) * 100),
        })

        await this.client.createSheet(config.name, config.headers)
      }

      // 이미 있는 시트에 대해서는 createSheet 호출 안 함

      // Step 6: 데이터 삽입
      currentStep++
      onProgress?.({
        step: 'inserting_data',
        totalSheets: this.SHEET_CONFIGS.length,
        completedSheets: this.SHEET_CONFIGS.length,
        message: '기본 데이터 삽입 중...',
        percentage: Math.round((currentStep / totalSteps) * 100),
      })

      await this.insertDefaultData()

      // Step 7: 마무리
      currentStep++
      onProgress?.({
        step: 'finalizing',
        totalSheets: this.SHEET_CONFIGS.length,
        completedSheets: this.SHEET_CONFIGS.length,
        message: '초기화 완료',
        percentage: 100,
      })

      onProgress?.({
        step: 'completed',
        totalSheets: this.SHEET_CONFIGS.length,
        completedSheets: this.SHEET_CONFIGS.length,
        message: '모든 작업이 완료되었습니다',
        percentage: 100,
      })

      // 성공 메시지 생성
      const summaryParts: string[] = []

      if (existingSheetsFound.length > 0) {
        summaryParts.push(`기존 시트 ${existingSheetsFound.length}개 유지됨`)
      }

      if (sheetsToCreate.length > 0) {
        summaryParts.push(`새 시트 ${sheetsToCreate.length}개 추가됨`)
      }

      const summaryMessage =
        summaryParts.length > 0
          ? ` (${summaryParts.join(', ')})`
          : ' (모든 시트가 이미 존재함)'

      return {
        success: true,
        message: `시트 확인 완료${summaryMessage}`,
        backedUpSheets: [], // 하위 호환성: 빈 배열
        existingSheets: existingSheetsFound.map((c) => c.name),
        createdSheets: sheetsToCreate.map((c) => c.name),
      }
    } catch (error) {
      return {
        success: false,
        message: `시트 추가 실패: ${error instanceof Error ? error.message : '알 수 없는 오류'}`,
      }
    }
  }

  /**
   * 기본 데이터 삽입
   *
   * 모드에 따라 적절한 데이터를 삽입합니다:
   * - test: 전체 Mock 데이터
   * - production: 필수 데이터만
   */
  private async insertDefaultData(): Promise<void> {
    if (this.mode === 'test') {
      await this.insertTestData()
    } else {
      await this.insertProductionData()
    }
  }

  /**
   * Google Sheets에 카테고리가 이미 존재하는지 확인
   *
   * @returns true: 카테고리 존재 (1개 이상), false: 비어있음
   *
   * @fix BUG-002, BUG-006: 재초기화 시 카테고리 중복 추가 방지
   */
  private async hasCategoriesInSheet(): Promise<boolean> {
    try {
      const rows = await this.client.readSheet(SHEET_NAMES.CATEGORIES)

      // 헤더만 있거나 빈 시트 = 카테고리 없음
      if (rows.length <= 1) {
        return false
      }

      // 데이터 행이 있으면 카테고리 존재
      const dataRows = rows.slice(1).filter((row) => row.length > 0 && row[0])
      return dataRows.length > 0
    } catch (error) {
      // 시트가 아직 생성되지 않은 경우
      console.log('[SpreadsheetInitializer] 카테고리 시트 없음 (신규 워크스페이스)')
      return false
    }
  }

  /**
   * README 시트에 데이터가 이미 존재하는지 확인
   *
   * @returns true: README 데이터 존재 (1개 이상), false: 비어있음
   *
   * @fix BUG-010: 재초기화 시 README 중복 추가 방지
   * @why 카테고리와 동일한 패턴으로 중복 방지 로직 적용
   */
  private async hasReadmeInSheet(): Promise<boolean> {
    try {
      const rows = await this.client.readSheet(SHEET_NAMES.README)

      // 헤더만 있거나 빈 시트 = README 없음
      if (rows.length <= 1) {
        return false
      }

      // 데이터 행이 있으면 README 존재
      const dataRows = rows.slice(1).filter((row) => row.length > 0 && row[0])
      return dataRows.length > 0
    } catch (error) {
      // 시트가 아직 생성되지 않은 경우
      console.log('[SpreadsheetInitializer] README 시트 없음 (신규 워크스페이스)')
      return false
    }
  }

  /**
   * README 시트에 워크스페이스 정보 및 경고문 삽입
   *
   * 스프레드시트의 첫 번째 시트로, 다음 정보를 포함:
   * - 워크스페이스 기본 정보 (이름, 생성일, 소유자, 멤버)
   * - 경고 메시지 (수동 수정 금지)
   * - 시트 구조 설명
   *
   * @param workspaceName - 워크스페이스 이름
   * @param ownerName - 소유자 이름
   * @param members - 멤버 이름 목록
   */
  private async insertReadmeData(
    workspaceName: string,
    ownerName: string,
    members: string[]
  ): Promise<void> {
    const now = new Date().toISOString().split('T')[0] || '' // YYYY-MM-DD 형식

    const readmeRows: string[][] = [
      // === 워크스페이스 정보 ===
      ['📊 Workspace Name', workspaceName],
      ['📅 Created Date', now],
      ['👤 Owner', ownerName],
      ['👥 Members', members.join(', ')],
      ['', ''], // 빈 줄

      // === 중요 경고 ===
      ['⚠️ WARNING', 'Do NOT manually edit sheets below!'],
      ['⚠️ WARNING', 'Use the ourWallet application ONLY'],
      ['🔒 IMPORTANT', 'Manual edits may corrupt data'],
      ['💾 DATA SAFETY', 'All changes must go through the app'],
      ['📱 APPLICATION', 'Use mobile/web app for all operations'],
      ['', ''], // 빈 줄

      // === 시트 설명 ===
      ['📚 SHEET REFERENCE', 'DESCRIPTION'],
      ['', ''], // 빈 줄
      ['📋 Categories', 'Expense/Income categories (2-level hierarchy)'],
      ['', '  - Major categories: Food, Transport, etc.'],
      ['', '  - Minor categories: Restaurant, Taxi, etc.'],
      ['', ''],
      ['💳 PaymentMethods', 'Payment methods information'],
      ['', '  - Cards, bank accounts, e-wallets'],
      ['', '  - Owner, type, memo fields'],
      ['', ''],
      ['👥 Users', 'Workspace members and access control'],
      ['', '  - Email, name, role (Owner/Member)'],
      ['', '  - Join date and active status'],
      ['', ''],
      ['💰 Budgets', 'Monthly budget limits per category'],
      ['', '  - Month, category, budget amount'],
      ['', '  - Used for budget tracking features'],
      ['', ''],
      ['📝 Transactions', 'All income/expense transaction records'],
      ['', '  - Date, type, amount, category, payment method'],
      ['', '  - Spender, memo, and other metadata'],
    ]

    // README 시트에 데이터 추가
    await this.client.appendSheet(SHEET_NAMES.README, readmeRows)
  }

  /**
   * Test 모드 데이터 삽입
   *
   * 모든 Mock 데이터를 삽입합니다:
   * - README: 워크스페이스 정보 및 경고문 (Mock 데이터)
   * - 카테고리: expenseCategories + incomeCategories (50+개)
   * - 사용자: mockUsers 전체 (3명)
   * - 결제수단: mockPaymentMethods 전체 (10개)
   * - 예산: mockBudgets 전체
   * - 거래내역: mockTransactions 전체 (70개)
   */
  private async insertTestData(): Promise<void> {
    // README 데이터 삽입 (첫 번째 시트)
    // ✅ README가 이미 있는지 확인 (BUG-010 수정)
    const hasReadme = await this.hasReadmeInSheet()

    if (!hasReadme) {
      const workspaceName = 'Test Wallet'
      const ownerName = mockUsers[0]?.name || 'Test Owner' // 첫 번째 Mock 사용자
      const members = mockUsers.map((u) => u.name)
      await this.insertReadmeData(workspaceName, ownerName, members)
      console.log('[SpreadsheetInitializer] 테스트 모드: README 데이터 삽입 완료')
    } else {
      console.log('[SpreadsheetInitializer] 테스트 모드: README 이미 존재, 생략')
    }

    // ✅ 카테고리가 이미 있는지 확인 (BUG-002, BUG-006 수정)
    const hasCategories = await this.hasCategoriesInSheet()

    const paymentMethodRows = convertPaymentMethodsToSheetRows(mockPaymentMethods)
    const userRows = convertUsersToSheetRows(mockUsers)
    const budgetRows = convertBudgetsToSheetRows(mockBudgets)
    const transactionRows = convertTransactionsToSheetRows(mockTransactions)

    // appendSheet를 사용하여 헤더 다음에 데이터 추가 (헤더를 덮어쓰지 않음)
    if (hasCategories) {
      console.log('[SpreadsheetInitializer] 테스트 모드: 카테고리 이미 존재, 생략')
    } else {
      console.log('[SpreadsheetInitializer] 테스트 모드: 카테고리 삽입 중...')

      const allCategories = [...expenseCategories, ...incomeCategories]
      const categoryRows = convertCategoriesToSheetRows(allCategories)

      await this.client.appendSheet(SHEET_NAMES.CATEGORIES, categoryRows)
      console.log(`[SpreadsheetInitializer] 카테고리 ${categoryRows.length}개 삽입 완료`)
    }
    await this.client.appendSheet(SHEET_NAMES.PAYMENT_METHODS, paymentMethodRows)
    await this.client.appendSheet(SHEET_NAMES.USERS, userRows)
    await this.client.appendSheet(SHEET_NAMES.BUDGETS, budgetRows)
    await this.client.appendSheet(SHEET_NAMES.TRANSACTIONS, transactionRows)
  }

  /**
   * Production 모드 데이터 삽입
   *
   * 필수 데이터만 삽입합니다:
   * - README: 워크스페이스 정보 및 경고문
   * - 카테고리: 대분류 + 세부 분류 전체
   *   - 지출: 10개 대분류 (식비, 교통, 쇼핑, 주거, 의료, 여가, 교육, 경조사, 금융, 기타) + 세부 분류
   *   - 수입: 3개 대분류 (급여, 부수입, 기타) + 세부 분류
   * - 사용자: 로그인한 사용자 1명 (member 역할)
   * - 결제수단: 빈 시트 (헤더만)
   * - 예산: 빈 시트 (헤더만)
   * - 거래내역: 빈 시트 (헤더만)
   */
  private async insertProductionData(): Promise<void> {
    // README 데이터 삽입 (첫 번째 시트)
    // ✅ README가 이미 있는지 확인 (BUG-010 수정)
    const hasReadme = await this.hasReadmeInSheet()

    if (!hasReadme) {
      const workspaceName = 'My Wallet' // 기본값
      const ownerName = this.currentUser?.name || 'Owner'
      const members = this.currentUser ? [this.currentUser.name] : []
      await this.insertReadmeData(workspaceName, ownerName, members)
      console.log('[SpreadsheetInitializer] 프로덕션 모드: README 데이터 삽입 완료')
    } else {
      console.log('[SpreadsheetInitializer] 프로덕션 모드: README 이미 존재, 생략')
    }

    // ✅ 카테고리가 이미 있는지 확인 (BUG-002, BUG-006 수정)
    const hasCategories = await this.hasCategoriesInSheet()

    if (hasCategories) {
      console.log('[SpreadsheetInitializer] 카테고리가 이미 존재합니다. 추가 생략.')
    } else {
      console.log('[SpreadsheetInitializer] 카테고리 삽입 중...')

      const allCategories = [...expenseCategories, ...incomeCategories]
      const categoryRows = convertCategoriesToSheetRows(allCategories)

      await this.client.appendSheet(SHEET_NAMES.CATEGORIES, categoryRows)
      console.log(`[SpreadsheetInitializer] 카테고리 ${categoryRows.length}개 삽입 완료`)
    }

    // 로그인한 사용자를 member로 등록
    if (this.currentUser) {
      const currentUserRow = [
        [
          this.currentUser.id,
          this.currentUser.email,
          this.currentUser.name,
          'member', // User decision: "member로 등록"
          'true',
          new Date().toISOString(),
        ],
      ]
      await this.client.appendSheet(SHEET_NAMES.USERS, currentUserRow)
    }
  }
}
